/** Stat cards and the progress tab. */
import { must } from '../dom';
import {
  byDate,
  formatMetric,
  formatTime,
  formatWorkout,
  numericValues,
  primaryMetric,
  shortDisplayName,
} from '../lib/format';
import { currentExercise, currentWorkouts, metricsFor } from '../lib/store';

export function updateStats(): void {
  const data = byDate(currentWorkouts());
  const lastEl = must('last-reps');
  const avgEl = must('avg-reps');

  const lastWorkout = data.at(-1);
  if (!lastWorkout) {
    lastEl.textContent = '0';
    avgEl.textContent = '0';
    return;
  }

  lastEl.textContent = formatWorkout(lastWorkout) || '0';

  const metric = primaryMetric(metricsFor(currentExercise));
  const values = numericValues(data, metric);
  if (values.length === 0) {
    avgEl.textContent = '0';
    return;
  }

  const mean = values.reduce((sum, value) => sum + value, 0) / values.length;
  avgEl.textContent = metric === 'time' ? formatTime(Math.round(mean)) : formatMetric(mean, metric);
}

export function showStatsView(): void {
  must('stats-view-btn').classList.add('active');
  must('chart-view-btn').classList.remove('active');
  must('stats-view').style.display = 'block';
  must('chart-view').style.display = 'none';
}

/**
 * Chart.js is most of the app's JavaScript (about 54 KB gzipped against 21 KB
 * for everything else), so the chart module is a separate file fetched the
 * first time this view opens, not on every page load. Cached after that.
 */
let chartModule: Promise<typeof import('./chart')> | null = null;

export async function showChartView(): Promise<void> {
  must('chart-view-btn').classList.add('active');
  must('stats-view-btn').classList.remove('active');
  must('chart-view').style.display = 'block';
  must('stats-view').style.display = 'none';

  const loading = must('chart-loading');
  const failed = must('chart-failed');
  failed.hidden = true;
  if (!chartModule) loading.hidden = false;

  try {
    chartModule ??= import('./chart');
    const { updateChart } = await chartModule;
    updateChart();
  } catch {
    // The one way this fails: first-ever open while offline, before the file
    // has been cached. Dropping the promise lets the next tap retry.
    chartModule = null;
    must('chart-container').hidden = true;
    must('chart-empty').hidden = true;
    failed.hidden = false;
  } finally {
    loading.hidden = true;
  }
}

export function updateProgressView(): void {
  updateStatsView();
}

/**
 * Fills the slots of the static markup in index.html rather than rebuilding
 * the view: #workout-history sits inside it and carries a delegated click
 * handler that an innerHTML rebuild would silently drop.
 *
 * Average is deliberately not here. It is on the log card, directly above,
 * and showing the same number twice on one screen was the complaint.
 */
export function updateStatsView(): void {
  const data = byDate(currentWorkouts());

  must('stats-empty').hidden = data.length > 0;
  must('stats-body').hidden = data.length === 0;
  if (data.length === 0) return;

  const metric = primaryMetric(metricsFor(currentExercise));
  const values = numericValues(data, metric);

  let best = '0';
  let improvement = '0';

  if (values.length > 0) {
    const first = values[0] ?? 0;
    const last = values.at(-1) ?? 0;
    const delta = values.length > 1 ? last - first : 0;

    best = formatMetric(Math.max(...values), metric);
    improvement =
      metric === 'time'
        ? `${delta > 0 ? '+' : ''}${Math.round(delta)} sec`
        : `${delta > 0 ? '+' : ''}${formatMetric(delta, metric)}`;
  }

  must('stats-title').textContent = `${shortDisplayName(currentExercise)} Progress`;
  must('stat-best').textContent = best;
  must('stat-improvement').textContent = improvement;

  const noun = data.length === 1 ? 'workout' : 'workouts';
  must('history-heading').textContent = `History · ${data.length} ${noun}`;
}
